import React, { useState } from 'react';

export type AvatarShape = 'square' | 'circle';

export interface AvatarProps {
    /** Image URL (logo/photo). Falls back to the initial of `name` when missing or it fails to load. */
    src?: string;
    /** Used for the initial fallback and as the image alt / accessible label. */
    name?: string;
    /** Pixel size of the (square) avatar. */
    size?: number;
    shape?: AvatarShape;
    className?: string;
}

// Deterministic, evenly-distributed hue from the label so the same entity always gets
// the same colour. Saturation/lightness are fixed for a consistent, on-brand feel.
// NOTE: avatar identity colours (this hue and the white text below) are intentionally
// NOT theme tokens — they are a generated identity palette, an explicit exception to
// the "colours come from theme tokens" rule (there is no avatar-colour token group).
const backgroundFor = (seed: string): string => {
    let hash = 0;
    for (let index = 0; index < seed.length; index += 1) {
        hash = seed.charCodeAt(index) + ((hash << 5) - hash);
    }
    return `hsl(${Math.abs(hash) % 360}, 55%, 52%)`;
};

/**
 * A logo/initial avatar. Renders `src` when available, otherwise a coloured tile
 * with the first letter of `name`. Encapsulates the fallback colour so consumers
 * never hand-roll it.
 */
const Avatar: React.FC<AvatarProps> = ({
    src,
    name,
    size = 24,
    shape = 'square',
    className = '',
}) => {
    // Track which src failed (not a boolean) so switching `src` to a new URL retries the
    // image instead of staying stuck on the fallback.
    const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
    const label = name?.trim() ?? '';
    const borderRadius = shape === 'circle' ? '50%' : `${Math.round(size * 0.25)}px`;

    if (src && failedSrc !== src) {
        return (
            <img
                src={src}
                alt={label}
                loading="lazy"
                decoding="async"
                onError={() => setFailedSrc(src)}
                className={className}
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius,
                    objectFit: 'contain',
                    flexShrink: 0,
                }}
            />
        );
    }

    // With a label the tile is an image with an accessible name; without one it carries
    // no information, so mark it decorative (aria-hidden, no role) to avoid an unnamed
    // image in the accessibility tree.
    return (
        <div
            {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
            className={className}
            style={{
                width: `${size}px`,
                height: `${size}px`,
                flexShrink: 0,
                borderRadius,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: backgroundFor(label),
                color: '#ffffff',
                fontWeight: 600,
                fontSize: `${Math.round(size * 0.45)}px`,
                lineHeight: 1,
                textTransform: 'uppercase',
            }}
        >
            {label.charAt(0) || '?'}
        </div>
    );
};

export default Avatar;
export { Avatar };
